import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { Check, X, ArrowRight, Zap } from 'lucide-react';
import { competitors } from '../data/competitors';

/**
 * Data-driven competitor comparison page (e.g. /roofr-alternative).
 * Content + schema come from src/data/competitors.js so the interactive page
 * and the prerendered crawler HTML (prerender.js) stay in sync.
 */
export default function CompetitorAlternative({ slug }) {
  const navigate = useNavigate();
  const { isAuthenticated, openAuthModal } = useAuth();
  const c = competitors[slug];
  if (!c) return null;

  const handleStart = () => {
    if (isAuthenticated) navigate('/dashboard');
    else openAuthModal('signup');
  };

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: `SquareCount — ${c.name} Alternative`,
    description: c.metaDescription,
    brand: { '@type': 'Brand', name: 'SquareCount' },
    image: 'https://squarecount.io/logo.png',
    url: `https://squarecount.io/${c.slug}`,
    offers: {
      '@type': 'Offer',
      price: '19.00',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      url: 'https://squarecount.io/pricing',
      description: 'Unlimited satellite roof measurements with PDF reports',
    },
  };
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: c.faq.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{c.metaTitle}</title>
        <meta name="description" content={c.metaDescription} />
        <meta property="og:title" content={c.metaTitle} />
        <meta property="og:description" content={c.metaDescription} />
        <link rel="canonical" href={`https://squarecount.io/${c.slug}`} />
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-6 py-20 lg:py-24">
          <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/30 rounded-full px-4 py-2 mb-6">
            <Zap className="w-4 h-4 text-orange-400" />
            <span className="text-orange-300 text-sm font-medium">{c.name} alternative · unlimited reports $19/mo</span>
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight mb-6">
            {c.h1}
          </h1>
          <p className="text-xl text-slate-300 mb-8 max-w-3xl">{c.intro}</p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              onClick={() => navigate('/pricing')}
              className="bg-orange-500 hover:bg-orange-400 text-white h-14 px-8 font-heading font-bold tracking-wide text-lg"
            >
              See Pricing — $19/mo <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              onClick={handleStart}
              className="h-14 px-8 font-heading font-bold tracking-wide text-lg border-2 border-white/20 text-white hover:bg-white/10 bg-transparent"
            >
              Start Your 7-Day Contractor Trial
            </Button>
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="font-heading font-black text-3xl text-slate-900 mb-8 text-center">
          SquareCount vs {c.name}
        </h2>
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-left">
            <thead className="bg-slate-50">
              <tr>
                <th className="p-4 font-heading font-bold text-slate-700">Feature</th>
                <th className="p-4 font-heading font-bold text-orange-600">SquareCount</th>
                <th className="p-4 font-heading font-bold text-slate-500">{c.name}</th>
              </tr>
            </thead>
            <tbody>
              {c.rows.map((row, i) => (
                <tr key={i} className={i % 2 ? 'bg-white' : 'bg-slate-50/50'}>
                  <td className="p-4 font-medium text-slate-700">{row[0]}</td>
                  <td className="p-4 text-slate-900 font-semibold">
                    <span className="inline-flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500 shrink-0" />{row[1]}
                    </span>
                  </td>
                  <td className="p-4 text-slate-500">{row[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Why switch */}
      <section className="bg-slate-50">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <h2 className="font-heading font-black text-3xl text-slate-900 mb-8">
            Why roofing contractors switch from {c.name}
          </h2>
          <ul className="grid sm:grid-cols-2 gap-4">
            {c.whySwitch.map((item, i) => (
              <li key={i} className="flex items-start gap-3 bg-white rounded-xl p-5 border border-slate-200">
                <Check className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" />
                <span className="text-slate-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 py-16">
        <h2 className="font-heading font-black text-3xl text-slate-900 mb-8">
          {c.name} alternative — frequently asked questions
        </h2>
        <div className="space-y-6">
          {c.faq.map((f, i) => (
            <div key={i} className="border-b border-slate-200 pb-6">
              <h3 className="font-heading font-bold text-lg text-slate-900 mb-2">{f.q}</h3>
              <p className="text-slate-600">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <h2 className="font-heading font-black text-3xl mb-4">
            Ready to ditch per-report fees?
          </h2>
          <p className="text-slate-300 mb-8">
            Measure your first roof free. Unlimited reports for $19/month. No contract, cancel anytime.
          </p>
          <Button
            onClick={handleStart}
            className="bg-orange-500 hover:bg-orange-400 text-white h-14 px-10 font-heading font-bold tracking-wide text-lg"
          >
            Start Your 7-Day Contractor Trial <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
